import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { Button, Group, Stack, Textarea } from "@mantine/core";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import StarRating from "./StarRating";

interface ReviewFormProps {
  /** Existing rating (for editing) */
  initialRating?: number;
  /** Existing review text (for editing) */
  initialReviewText?: string;
  /** Callback when form is submitted */
  onSubmit: (rating: number, reviewText: string, turnstileToken: string) => Promise<void>;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
  /** Submit button text */
  submitText?: string;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
}

export const ReviewForm: FunctionComponent<ReviewFormProps> = ({
  initialRating = 0,
  initialReviewText = "",
  onSubmit,
  onCancel,
  submitText = "Submit Review",
  isSubmitting = false,
}) => {
  const [rating, setRating] = useState(initialRating);
  const [reviewText, setReviewText] = useState(initialReviewText);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileInstance>(null);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  // Update form state when initial props change (e.g., when editing a review)
  useEffect(() => {
    setRating(initialRating);
    setReviewText(initialReviewText);
  }, [initialRating, initialReviewText]);

  const handleSubmit = async () => {
    if (rating === 0 || !turnstileToken) {
      return;
    }
    await onSubmit(rating, reviewText, turnstileToken);
    // Reset turnstile after submission
    turnstileRef.current?.reset();
  };

  return (
    <Stack gap="md">
      <div>
        <StarRating
          value={rating}
          readOnly={false}
          onChange={setRating}
          size="lg"
          showCount={false}
        />
      </div>

      <Textarea
        placeholder="Share your experience (optional)"
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        autosize
        minRows={3}
        maxRows={15}
        maxLength={2000}
      />

      {/* Cloudflare Turnstile for spam protection */}
      {siteKey && (
        <Turnstile
          ref={turnstileRef}
          siteKey={siteKey}
          onSuccess={(token) => setTurnstileToken(token)}
          onError={() => setTurnstileToken("")}
          onExpire={() => setTurnstileToken("")}
        />
      )}

      <Group>
        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || !turnstileToken || isSubmitting}
          loading={isSubmitting}
        >
          {submitText}
        </Button>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  );
};

export default ReviewForm;
